import React, { createContext, useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { setQuantityPricingConfig } from '../store/cartSlice';
import { getEffectiveUnitPrice, getPricingScopeKey } from '../utils/quantityPricing';

export const QuantityPricingContext = createContext();

export { getEffectiveUnitPrice };

export const QuantityPricingProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [breakpoints, setBreakpoints] = useState([1, 2, 6, 12]);
  const [categoriesPricing, setCategoriesPricing] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchQuantityPricing = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL}/quantity-pricing`);
      if (!response.ok) throw new Error('Error al cargar precios por cantidad');
      const data = await response.json();
      setBreakpoints(data.breakpoints || [1, 2, 6, 12]);
      setCategoriesPricing(data.categories || []);

      // Sincroniza los tiers válidos por categoría con el carrito (Redux), para que
      // el descuento por cantidad se calcule sobre el total de la categoría en el
      // carrito, aunque combine productos distintos de esa misma categoría.
      const configByScope = {};
      (data.categories || []).forEach(cat => {
        const validTiers = (cat.tiers || []).filter(t => t.price !== null && t.price !== undefined);
        if (cat.configured && validTiers.length > 0) {
          configByScope[cat.pricingKey || getPricingScopeKey(cat.categoryId, cat.subcategory)] = validTiers;
        }
      });
      dispatch(setQuantityPricingConfig(configByScope));
    } catch (error) {
      console.error('Error fetching quantity pricing:', error);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => { fetchQuantityPricing(); }, [fetchQuantityPricing]);

  // Devuelve los tiers configurados y activos para una categoría, o [] si no tiene.
  const getTiersForCategory = (categoryId, subcategory = '') => {
    if (!categoryId) return [];
    const pricingKey = getPricingScopeKey(categoryId, subcategory);
    const entry = categoriesPricing.find(category => (
      (category.pricingKey || getPricingScopeKey(category.categoryId, category.subcategory)) === pricingKey
    ));
    if (!entry || !entry.configured) return [];
    return entry.tiers.filter(t => t.price !== null && t.price !== undefined);
  };

  const getUnitPrice = (categoryId, subcategory, quantity, basePrice) => {
    const tiers = getTiersForCategory(categoryId, subcategory);
    return getEffectiveUnitPrice(tiers, quantity, basePrice);
  };

  const value = {
    breakpoints,
    categoriesPricing,
    loading,
    refreshQuantityPricing: fetchQuantityPricing,
    getTiersForCategory,
    getUnitPrice
  };

  return (
    <QuantityPricingContext.Provider value={value}>
      {children}
    </QuantityPricingContext.Provider>
  );
};
